import type { CurrencyCode } from '@shared/contracts/entities';
import { nowMs } from './clock';

/**
 * Shared financial formatters.
 *
 * Definition of done: "Financial amounts, status and timestamps use shared
 * formatters." Design rule: financial values use tabular numerals and never
 * animate through fake intermediate balances.
 *
 * Locale is fixed to en-GB — the package market baseline is UK/Europe.
 */
const LOCALE = 'en-GB';

const currencyFormatters = new Map<string, Intl.NumberFormat>();

function currencyFormatter(currency: CurrencyCode): Intl.NumberFormat {
  const key = currency;
  let fmt = currencyFormatters.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(LOCALE, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    currencyFormatters.set(key, fmt);
  }
  return fmt;
}

/**
 * Minor units in, formatted string out. All fixture amounts are integer minor
 * units so no floating-point rounding can reach a displayed balance.
 */
export function formatAmount(minorUnits: number, currency: CurrencyCode): string {
  return currencyFormatter(currency).format(minorUnits / 100);
}

/**
 * Signed presentation. Rule: "Negative and positive values use text/sign plus
 * semantic styling" — the sign is always in the text, never colour alone.
 */
export function formatSignedAmount(minorUnits: number, currency: CurrencyCode): string {
  const abs = formatAmount(Math.abs(minorUnits), currency);
  if (minorUnits > 0) return `+${abs}`;
  if (minorUnits < 0) return `−${abs}`;
  return abs;
}

/** Screen-reader text for an amount, avoiding symbol ambiguity. */
export function amountAccessibleLabel(minorUnits: number, currency: CurrencyCode): string {
  const sign = minorUnits > 0 ? 'plus ' : minorUnits < 0 ? 'minus ' : '';
  const value = new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(minorUnits) / 100);
  return `${sign}${value} ${currency}`;
}

export function formatDate(iso: string): string {
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(iso));
}

export function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

export function formatTime(iso: string): string {
  return new Intl.DateTimeFormat(LOCALE, { hour: '2-digit', minute: '2-digit' }).format(
    new Date(iso),
  );
}

/** Day grouping label for transaction feeds: Today / Yesterday / date. */
export function formatDayGroup(iso: string): string {
  const day = new Date(iso);
  const today = new Date(nowMs());
  const startOf = (d: Date) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const diffDays = Math.round((startOf(today) - startOf(day)) / 86_400_000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return formatDate(iso);
}

/**
 * Percentage change with a mandatory comparison period.
 *
 * TT-R103-02 requires every percentage-change indicator to state the period it
 * compares against, so the period is a required argument — a caller physically
 * cannot render a bare percentage through this formatter.
 */
export function formatPercentageChange(
  changeRatio: number,
  comparisonPeriod: string,
): { value: string; period: string; accessibleLabel: string } {
  const pct = Math.abs(changeRatio * 100);
  const rounded = pct >= 10 ? pct.toFixed(0) : pct.toFixed(1);
  const sign = changeRatio > 0 ? '+' : changeRatio < 0 ? '−' : '';
  const value = `${sign}${rounded}%`;
  const direction = changeRatio > 0 ? 'up' : changeRatio < 0 ? 'down' : 'unchanged';
  return {
    value,
    period: comparisonPeriod,
    accessibleLabel:
      changeRatio === 0
        ? `unchanged ${comparisonPeriod}`
        : `${direction} ${rounded}% ${comparisonPeriod}`,
  };
}

/** Masked PAN. Sensitive data is masked by default; reveal is a separate action. */
export function maskedPan(last4: string): string {
  return `•••• •••• •••• ${last4}`;
}

/** IBAN in the conventional 4-character groups, for readability when shared. */
export function formatIban(iban: string): string {
  return iban.replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim();
}

export function formatSortCode(sortCode: string): string {
  const digits = sortCode.replace(/\D/g, '');
  return digits.length === 6 ? `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 6)}` : sortCode;
}

/** Sentence-case a canonical kebab status for display, without inventing wording. */
export function statusLabel(status: string): string {
  const words = status.split('-');
  const [first, ...rest] = words;
  if (first === undefined) return status;
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ');
}
